// components/dashboard/AixaDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useAuthSimple } from '../../hooks/useAuth';
import { EmpresaList } from '../empresa/EmpresaList';
import { Navbar } from '../layouts/Navbar';
import { PlannerSidebar } from '../layouts/PlannerSidebar';
import { ResumoCards } from './ResumoCards';
import DashboardCharts from './DashboardCharts';
import FinanceiroCards from './FinanceiroCards';
import FinanceiroCharts from './FinanceiroCharts';
import ClientesCards from './ClientesCards';
import DocumentosCards from './DocumentosCards';
import ParcelasCards from './ParcelasCards';
import SinistrosCards from './SinistrosCards';
import RepassesCards from './RepassesCards';
import { useNotifier } from '../ui/Notifier';
import { apiClient } from '../../config/api';

export const AixaDashboard: React.FC = () => {
  const { user, isMaster, isGrupo, isEmpresa } = useAuthSimple();
  const notifier = useNotifier();
  const now = new Date();
  const defaultMonth = String(now.getMonth() + 1).padStart(2, '0');
  const defaultYear = String(now.getFullYear());
  const [month, setMonth] = useState<string>(defaultMonth);
  const [year, setYear] = useState<string>(defaultYear);
  
  const months = [
    { value: '', label: 'Todos os Meses' },
    { value: '01', label: '01 - Janeiro' },
    { value: '02', label: '02 - Fevereiro' },
    { value: '03', label: '03 - Março' },
    { value: '04', label: '04 - Abril' },
    { value: '05', label: '05 - Maio' },
    { value: '06', label: '06 - Junho' },
    { value: '07', label: '07 - Julho' },
    { value: '08', label: '08 - Agosto' },
    { value: '09', label: '09 - Setembro' },
    { value: '10', label: '10 - Outubro' },
    { value: '11', label: '11 - Novembro' },
    { value: '12', label: '12 - Dezembro' },
  ];
  
  const currentYear = new Date().getFullYear();
  const years = [{ value: '', label: 'Todos os Anos' }];
  for (let y = 2010; y <= currentYear + 1; y++) years.push({ value: String(y), label: String(y) });

  // Check SLA crítico on mount
  useEffect(() => {
    if (!user) return;
    const empresaId = (user as any).accessScope?.empresaId || (user as any).permissions?.empresaIds?.[0];
    if (!empresaId) return;
    apiClient.request<any>(`/dashboard/alertas/${empresaId}`).then((res: any) => {
      const data = res?.data;
      if (data?.sinistrosSLACritico > 0) {
        notifier.show({
          message: `⚠ ${data.sinistrosSLACritico} sinistro(s) com SLA crítico ou expirado!`,
          severity: 'warning',
        });
      }
    }).catch(() => {/* silent */});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const periodLabel = () => {
    if (!year && !month) return 'Período: Todos';
    if (year && month) return `Período: ${month}/${year}`;
    if (year) return `Período: ${year}`;
    return `Período: ${month}`;
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4EFFA' }}>
      <Navbar />
      <div className="flex p-4 gap-3">
        <div className="flex-1 space-y-4">
          <div className="mb-4">
            <h2 className="text-lg font-bold" style={{ color: '#3d2b56' }}>
              AIXA Dashboard
              {isGrupo && user?.grupoNome && (
                <span className="text-sm text-gray-600 ml-2">- Grupo: {user.grupoNome}</span>
              )}
              {isEmpresa && user?.empresaNome && (
                <span className="text-sm text-gray-600 ml-2">- Empresa: {user.empresaNome}</span>
              )}
            </h2>
            <p className="text-xs" style={{ color: '#9E9E9E' }}>
              {isGrupo ? 'Visão consolidada do grupo de empresas' : 'Sistema de Importação e Gerenciamento'}
            </p>
          </div>

          {/* Filtros */}
          <div className="bg-white p-3 rounded-lg shadow-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs font-medium" style={{ color: '#3d2b56' }}>Filtros:</label>
              <select 
                className="p-1.5 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                value={month} 
                onChange={(e) => setMonth(e.target.value)}
                style={{ borderColor: '#C3B4DE' }}
              >
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select 
                className="p-1.5 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                value={year} 
                onChange={(e) => setYear(e.target.value)}
                style={{ borderColor: '#C3B4DE' }}
              >
                {years.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
              </select>
              <button 
                type="button" 
                onClick={() => { setMonth(''); setYear(''); }} 
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                Limpar Filtros
              </button>
              <span className="text-xs text-gray-600 ml-auto">{periodLabel()}</span>
            </div>
          </div>

          {/* Resumo Geral */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#5d54a4' }}>Resumo Geral</h3>
            <ResumoCards year={year} month={month} />
            <DashboardCharts year={year} month={month} />
          </section>

          {/* Financeiro */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#5d54a4' }}>Indicadores Financeiros</h3>
            <FinanceiroCards year={year} month={month} />
            <FinanceiroCharts year={year} month={month} />
          </section>

          {/* Clientes */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#5d54a4' }}>Clientes</h3>
            <ClientesCards year={year} month={month} />
          </section>

          {/* Documentos */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#5d54a4' }}>Estado dos Documentos</h3>
            <DocumentosCards year={year} month={month} />
          </section>

          {/* Parcelas */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#5d54a4' }}>
              Parcelas {isGrupo ? '(Consolidado do Grupo)' : ''}
            </h3>
            <ParcelasCards year={year} month={month} />
          </section>

          {/* Sinistros */}
          <section>
            <h3 className="text-sm font-semibold mb-3" style={{ color: '#5d54a4' }}>
              Sinistros {isGrupo ? '(Consolidado do Grupo)' : ''}
            </h3>
            <SinistrosCards year={year} month={month} />
          </section>

          {/* Repasses */}
          <section>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#5d54a4' }}>
              Repasses {isGrupo ? '(Consolidado do Grupo)' : ''}
            </h3>
            <RepassesCards year={year} month={month} />
          </section>

          {/* Link para Importações */}
          <section>
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#5d54a4' }}>Ações Rápidas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="border rounded-md p-4 shadow-sm" style={{ backgroundColor: '#FFFFFF', borderColor: '#C3B4DE' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-md font-semibold" style={{ color: '#3d2b56' }}>Importações</h5>
                    <p className="text-xs" style={{ color: '#9E9E9E' }}>Importe dados de clientes, análise de produção e mais</p>
                  </div>
                  <div>
                    <a href="/importacoes" className="inline-flex items-center px-3 py-2 text-white rounded-md hover:opacity-80 transition-opacity text-sm" style={{ backgroundColor: '#7A61B2' }}>Acessar</a>
                  </div>
                </div>
              </div>

              <div className="border rounded-md p-4 shadow-sm" style={{ backgroundColor: '#FFFFFF', borderColor: '#C3B4DE' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-md font-semibold" style={{ color: '#3d2b56' }}>Clientes</h5>
                    <p className="text-xs" style={{ color: '#9E9E9E' }}>Gerencie sua base de clientes</p>
                  </div>
                  <div>
                    <a href="/clientes" className="inline-flex items-center px-3 py-2 text-white rounded-md hover:opacity-80 transition-opacity text-sm" style={{ backgroundColor: '#2eb897' }}>Acessar</a>
                  </div>
                </div>
              </div>

              <div className="border rounded-md p-4 shadow-sm" style={{ backgroundColor: '#FFFFFF', borderColor: '#C3B4DE' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-md font-semibold" style={{ color: '#3d2b56' }}>Apólices</h5>
                    <p className="text-xs" style={{ color: '#9E9E9E' }}>Consulte e gerencie apólices</p>
                  </div>
                  <div>
                    <a href="/apolice" className="inline-flex items-center px-3 py-2 text-white rounded-md hover:opacity-80 transition-opacity text-sm" style={{ backgroundColor: '#F9A826' }}>Acessar</a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {(isMaster || isGrupo) && (
            <section className="mb-8">
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900">Gerenciamento</h3>
                <p className="text-gray-600">Gerencie empresas e configurações do sistema</p>
                </div>

                <EmpresaList />
              </section>
            )}

            {isEmpresa && (
              <section className="mb-8">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-xs font-medium text-blue-800">Informações da Empresa</h4>
                      <div className="mt-2 text-xs text-blue-700">
                        <p>Empresa ID: <strong>{user.empresaId}</strong></p>
                        <p>Os dados importados serão automaticamente associados à sua empresa.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {isMaster && (
              <section className="mb-8">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-xs font-medium text-yellow-800">Acesso Master</h4>
                      <div className="mt-2 text-xs text-yellow-700">
                        <p>Você tem acesso completo ao sistema. Use com responsabilidade.</p>
                        <p>Pode gerenciar todas as empresas e importar dados para qualquer empresa.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
        </div>
        <div className="hidden lg:block">
          <PlannerSidebar />
        </div>
      </div>
    </div>
  );
};